'use client';

import { ArrowLeft, Check } from 'lucide-react';
import { m, useReducedMotion } from 'motion/react';

import { Button } from '@heidi/design-system/button';
import { useIsExperimentalDesignSystemButtons } from '@heidi/design-system/hooks/use-is-experimental-design-system-buttons';
import { useIsExperimentalDesignSystemForms } from '@heidi/design-system/hooks/use-is-experimental-design-system-forms';
import { Progress } from '@heidi/design-system/progress';
import { cn } from '@heidi/utils';

import { useOnboardingContext } from '../../context/OnboardingContext';

import { PreviewPanel } from './preview';

/**
 * Learn Basics steps built on `OnboardingStepShell`, which scrolls its own
 * content and keeps the footer actions on-screen. Their column is bounded to
 * the viewport so the shell has a fixed height to scroll within; every other
 * step keeps the `min-h-full` column that grows and scrolls as a whole.
 *
 * This is every step of the flow bar the control arm's `invite-team` and
 * `note-ready` (GRO-3927): the CTA then lands at the same viewport-relative
 * spot on each step instead of jumping to wherever the content ends, and
 * overflow scrolls under the action bar rather than pushing Continue below the
 * fold.
 *
 * Flow-scoped because `clinic` and `completion` are the ids both flows render:
 * V2 shows `CompletionStep`, which has no scroll region of its own, so bounding
 * its column would clip tall content (webinar / request-demo) instead of fixing
 * it, and its `clinic` keeps the grow-and-scroll column its neighbouring steps
 * use (see `hasPinnedFooter` on `ClinicStep`).
 */
const LEARN_BASICS_STEPS_WITH_OWN_SCROLL = new Set([
  'about-you',
  'clinic',
  'mic-test',
  'note-edit',
  'tour-invite-team',
  'tour-detail',
  'tour-artefacts',
  'tour-evidence',
  'completion',
]);

interface PersonalizationLayoutProps {
  children: React.ReactNode;
}

export function PersonalizationLayout({ children }: PersonalizationLayoutProps) {
  const {
    progressPercentage,
    currentStep,
    canGoBack,
    handleBack,
    flowKind,
    isGuidedTour,
  } = useOnboardingContext();
  const isCompletionStep = currentStep === 'completion';
  /**
   * The guided tour's completion step draws its own large success check beside
   * the heading, so a second check + full bar above it reads as duplicate
   * confirmation. Every other step keeps the progress header.
   */
  const hideProgressHeader = isCompletionStep && isGuidedTour;
  const hideBackOnMobile = currentStep === 'role';
  const managesOwnScroll =
    flowKind === 'learn-basics' && LEARN_BASICS_STEPS_WITH_OWN_SCROLL.has(currentStep);
  const prefersReducedMotion = useReducedMotion();
  const isExperimentalForms = useIsExperimentalDesignSystemForms();
  const isExperimentalButtons = useIsExperimentalDesignSystemButtons();
  const useReworkedSuccessTone = isExperimentalForms && isExperimentalButtons;

  return (
    <div className="relative flex h-dvh w-full bg-surface-accent p-4 lg:p-6">
      {canGoBack && (
        <Button
          variant="ghost"
          size="sm"
          iconOnly
          onClick={handleBack}
          className={cn(
            'absolute left-4 top-4 lg:left-6 lg:top-6',
            hideBackOnMobile && 'hidden lg:inline-flex'
          )}
        >
          <ArrowLeft size={16} className="rtl:-scale-x-100" />
        </Button>
      )}

      {/* Left side - Form content */}
      <div className="flex min-h-0 flex-1 basis-0 items-start justify-center overflow-y-auto lg:py-16 lg:pr-8">
        <div
          className={cn(
            'flex w-full max-w-[400px] flex-col gap-10',
            managesOwnScroll ? 'h-full min-h-0' : 'min-h-full'
          )}
        >
          {!hideProgressHeader && (
            <div className="flex w-full items-center justify-between gap-8">
              {isCompletionStep ? (
                <div className="flex items-center gap-2">
                  <m.div
                    className={cn(
                      'flex size-5 items-center justify-center rounded-full',
                      useReworkedSuccessTone
                        ? 'bg-semantic-fill-success'
                        : 'bg-validation-success'
                    )}
                    initial={prefersReducedMotion ? false : { scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{
                      type: 'spring',
                      stiffness: 400,
                      damping: 15,
                      delay: 0.2,
                    }}
                  >
                    <Check size={12} className="text-text-primary-invert" />
                  </m.div>
                  <Progress value={100} className="h-2 w-[187px]" />
                </div>
              ) : (
                <Progress value={progressPercentage} className="h-2 w-[187px]" />
              )}
            </div>
          )}

          {children}
        </div>
      </div>

      {/* Right side - Preview panel (hidden on mobile/tablet) */}
      <div className="hidden h-[calc(100dvh-48px)] flex-1 basis-0 lg:block">
        <PreviewPanel />
      </div>
    </div>
  );
}
